/**
 * **Die Leistung jetzt je Teil der Zusammensetzung** (HM-146, `loads.jsx` des
 * Entwurfs): ein schmaler zweiter Balken unter dem Energiebalken und eine
 * Spalte je Zeile. Dieselben Teile wie die Energie, aufgeteilt nach der
 * aktuellen Leistung ihrer Zähler.
 *
 * - **Gemessene Teile** (Verbraucher, Wohnungen) haben die Leistung ihres
 *   Zählers.
 * - **Der übrige Verbrauch ist auch hier gerechnet**: Gesamtleistung minus
 *   die Teile, nie unter null — dieselbe Regel wie bei der Energie.
 * - **Ohne gerechneten Rest ist das Ganze die Summe der Teile** (Mieterstrom,
 *   die Wohnungen untereinander): Der Verbrauch ist dort die Summe der
 *   Unterzähler.
 *
 * **Fehlt einem Teil der Wert, gibt es keine Leistung jetzt** — nicht für
 * diesen Teil, sondern für alle: Ein Rest ohne einen Summanden wäre zu groß,
 * und ein Balken ohne einen Streifen sähe vollständig aus. Eine halbe Bilanz
 * ist keine (wie in `live-now.ts`).
 *
 * **Und woher die Leistung kommt** (HM-150): je Teil der PV-Anteil jetzt,
 * gerechnet vom Server nach der Regel des Messkonzepts — dieselbe Regel wie
 * für die Energie. Der PV-Anteil des übrigen Verbrauchs ist der des Ganzen
 * ohne die der gemessenen Teile. Fehlt einem Teil der PV-Anteil, gibt es ihn
 * für keinen; die Leistung selbst bleibt.
 */

export interface LivePart {
  readonly key: string;
  /** Ob der Teil gerechnet ist (Gesamt minus die übrigen) statt gemessen. */
  readonly rest: boolean;
}

export interface CompositionLive {
  /** Die Leistung des Ganzen in Watt. */
  readonly totalW: number;
  /** Je Teil in Watt, nie negativ. */
  readonly byKey: ReadonlyMap<string, number>;
  /** Der PV-Anteil des Ganzen in Watt — `null`, wenn er nicht für jeden Teil vorliegt. */
  readonly pvW: number | null;
  /** Der PV-Anteil je Teil in Watt, höchstens seine Leistung — `null` wie `pvW`. */
  readonly pvByKey: ReadonlyMap<string, number> | null;
}

export interface LivePv {
  /** Der PV-Anteil eines gemessenen Teils in Watt. */
  readonly of: (key: string) => number | null;
  /** Der PV-Anteil des Ganzen — nur nötig, wo ein Teil gerechnet ist. */
  readonly totalW: number | null;
}

export function compositionLive(
  parts: readonly LivePart[],
  /** Die Leistung eines gemessenen Teils in Watt — `null`, wenn kein frischer Wert vorliegt. */
  powerOf: (key: string) => number | null,
  /** Die Leistung des Ganzen — nur nötig, wo ein Teil gerechnet ist. */
  totalW: number | null,
  pv: LivePv | null = null,
): CompositionLive | null {
  if (parts.length === 0) return null;
  const byKey = new Map<string, number>();
  let measured = 0;
  for (const part of parts) {
    if (part.rest) continue;
    const watts = powerOf(part.key);
    if (watts === null || !Number.isFinite(watts)) return null;
    const clamped = Math.max(watts, 0);
    byKey.set(part.key, clamped);
    measured += clamped;
  }
  const restParts = parts.filter((part) => part.rest);
  if (restParts.length > 1) return null;
  const rest = restParts[0];
  let whole = measured;
  if (rest !== undefined) {
    if (totalW === null || !Number.isFinite(totalW)) return null;
    byKey.set(rest.key, Math.max(totalW - measured, 0));
    whole = Math.max(totalW, measured);
  }
  const shares = pv === null ? null : pvShares(parts, byKey, pv, rest?.key ?? null);
  return { totalW: whole, byKey, pvW: shares?.total ?? null, pvByKey: shares?.byKey ?? null };
}

function pvShares(
  parts: readonly LivePart[],
  byKey: ReadonlyMap<string, number>,
  pv: LivePv,
  restKey: string | null,
): { total: number; byKey: ReadonlyMap<string, number> } | null {
  const shares = new Map<string, number>();
  let measured = 0;
  for (const part of parts) {
    if (part.rest) continue;
    const watts = pv.of(part.key);
    if (watts === null || !Number.isFinite(watts)) return null;
    const clamped = Math.min(Math.max(watts, 0), byKey.get(part.key) ?? 0);
    shares.set(part.key, clamped);
    measured += clamped;
  }
  if (restKey === null) return { total: measured, byKey: shares };
  if (pv.totalW === null || !Number.isFinite(pv.totalW)) return null;
  const restPv = Math.min(Math.max(pv.totalW - measured, 0), byKey.get(restKey) ?? 0);
  shares.set(restKey, restPv);
  return { total: measured + restPv, byKey: shares };
}
